import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Copy, Check, Loader2, Trash2, RefreshCw, CheckCircle2, Circle, MessageSquareText, Lightbulb, ImagePlus } from "lucide-react";
import { estraiCommenti, listCommenti, listScripts, patchCommento, rigeneraCommento, deleteCommento } from "@/lib/api";
import Ceste from "./Ceste";

const MAX_SIDE = 1400;

async function shrinkImage(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.82);
  } finally { URL.revokeObjectURL(url); }
}

const CopyBtn = ({ text, testid }) => {
  const [ok, setOk] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1200); } catch { toast.error("Copia non riuscita"); }
  };
  return (
    <button onClick={copy} data-testid={testid} title="Copia" className="shrink-0 p-1.5 rounded-md border border-[#262626] text-neutral-400 hover:text-white hover:border-[#404040] transition-colors">
      {ok ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
    </button>
  );
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short", timeZone: "Europe/Rome" });

function UploadBox({ scripts, onDone }) {
  const [reelId, setReelId] = useState("");
  const [fonte, setFonte] = useState("commento");
  const [progress, setProgress] = useState(null);
  const inputRef = useRef(null);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    e.target.value = "";
    if (!files.length) return;
    let trovati = 0;
    for (let i = 0; i < files.length; i++) {
      setProgress({ i: i + 1, n: files.length });
      try {
        const b64 = await shrinkImage(files[i]);
        const r = await estraiCommenti({ image_base64: b64, reel_id: reelId || null, fonte });
        trovati += r.n;
        onDone(r.commenti);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Screenshot ${i + 1}: non sono riuscito a leggerlo`);
      }
    }
    setProgress(null);
    toast[trovati ? "success" : "info"](trovati ? `${trovati} comment${trovati === 1 ? "o letto" : "i letti"}` : "Nessun commento leggibile negli screenshot");
  };

  return (
    <div className="rounded-lg border border-[#262626] bg-[#111] p-5" data-testid="commenti-upload">
      <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
        <div>
          <label className="block text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500 mb-1.5">Sotto quale reel? <span className="normal-case tracking-normal text-neutral-600">(facoltativo)</span></label>
          <select value={fonte === "dm" ? "__dm" : reelId} onChange={(e) => { if (e.target.value === "__dm") { setFonte("dm"); setReelId(""); } else { setFonte("commento"); setReelId(e.target.value); } }}
            data-testid="commenti-reel-select" className="w-full bg-[#0a0a0a] border border-[#262626] rounded-md px-3 py-2.5 text-sm text-white outline-none focus:border-[#D92D20]">
            <option value="">Non lo indico</option>
            <option value="__dm">È un DM (messaggio privato)</option>
            {scripts.map((s) => <option key={s.id} value={s.id}>{`${fmtDate(s.created_at)} · ${(s.hook_fissato || s.tema || "").slice(0, 80)}`}</option>)}
          </select>
        </div>
        <button onClick={() => inputRef.current?.click()} disabled={!!progress} data-testid="commenti-upload-btn"
          className="inline-flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#b8261b] disabled:opacity-60 text-white font-semibold text-sm px-5 py-2.5 rounded-md transition-colors">
          {progress ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
          {progress ? `Leggo ${progress.i}/${progress.n}…` : "Carica screenshot"}
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="commenti-file-input" />
      </div>
      <p className="mt-3 text-xs text-neutral-500">Screenshot dei commenti sotto il reel o di un DM, anche più d'uno insieme. Leggo il testo, ti preparo la risposta e, se c'è materiale, l'idea per un reel. I nomi di chi scrive non li salvo.</p>
    </div>
  );
}

function CommentCard({ c, onChange, onDelete }) {
  const [busy, setBusy] = useState(false);
  const [nota, setNota] = useState("");
  const [showNota, setShowNota] = useState(false);

  const toggleUsato = async () => {
    try { onChange(await patchCommento(c.id, { usato: !c.usato })); } catch { toast.error("Errore"); }
  };
  const rigenera = async () => {
    setBusy(true);
    try { onChange(await rigeneraCommento(c.id, nota)); setShowNota(false); setNota(""); toast.success("Risposta rifatta"); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  const elimina = async () => {
    try { await deleteCommento(c.id); onDelete(c.id); } catch { toast.error("Errore"); }
  };
  const idea = c.idea || {};

  return (
    <div className={`rounded-lg border bg-[#111] p-5 transition-colors ${c.usato ? "border-[#1f1f1f] opacity-60" : "border-[#262626]"}`} data-testid={`commento-${c.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex flex-wrap items-center gap-2 text-[0.65rem] uppercase tracking-[0.18em] text-neutral-500">
          <span>{fmtDate(c.created_at)}</span>
          <span>· {c.fonte === "dm" ? "DM" : "Commento"}</span>
          {c.reel_titolo && <span className="normal-case tracking-normal text-neutral-400 truncate max-w-[280px]" title={c.reel_titolo}>· sotto «{c.reel_titolo.slice(0, 60)}»</span>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={toggleUsato} data-testid={`commento-usato-${c.id}`} title={c.usato ? "Segna da usare" : "Segna come usato"}
            className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border transition-colors ${c.usato ? "border-emerald-700 text-emerald-400" : "border-[#262626] text-neutral-400 hover:text-white"}`}>
            {c.usato ? <CheckCircle2 size={13} /> : <Circle size={13} />} {c.usato ? "Usato" : "Da usare"}
          </button>
          <button onClick={elimina} data-testid={`commento-delete-${c.id}`} title="Elimina" className="p-1.5 rounded-md text-neutral-600 hover:text-[#D92D20] transition-colors"><Trash2 size={14} /></button>
        </div>
      </div>

      <blockquote className="border-l-2 border-[#404040] pl-3 text-sm text-neutral-200 leading-relaxed whitespace-pre-wrap" data-testid={`commento-testo-${c.id}`}>
        {c.testo}{c.troncato && <span className="text-neutral-600"> […]</span>}
      </blockquote>

      <div className="mt-4">
        <p className="text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500 mb-1">La tua risposta</p>
        <div className="flex items-start gap-2">
          <p className="text-base font-medium text-white leading-snug flex-1" data-testid={`commento-risposta-${c.id}`}>{c.risposta || "—"}</p>
          <CopyBtn text={c.risposta} testid={`commento-copy-${c.id}`} />
        </div>
        <div className="mt-2 flex items-center gap-3">
          <button onClick={() => setShowNota((v) => !v)} data-testid={`commento-rifai-${c.id}`} className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-200 transition-colors">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Rifai la risposta
          </button>
        </div>
        {showNota && (
          <div className="mt-2 flex gap-2">
            <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Come la vuoi? (facoltativo: più corta, più dura, cita il dato…)" data-testid={`commento-nota-${c.id}`}
              className="flex-1 bg-[#0a0a0a] border border-[#262626] rounded-md px-3 py-2 text-sm text-white placeholder:text-neutral-600 outline-none focus:border-[#D92D20]" />
            <button onClick={rigenera} disabled={busy} data-testid={`commento-rifai-go-${c.id}`} className="text-sm px-3 py-2 rounded-md bg-[#1a1a1a] border border-[#262626] text-white hover:border-[#404040] disabled:opacity-50">
              {busy ? "…" : "Vai"}
            </button>
          </div>
        )}
      </div>

      {idea.spunto && (
        <div className="mt-4 rounded-md border border-[#D92D20]/30 bg-[#D92D20]/5 p-4" data-testid={`commento-idea-${c.id}`}>
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb size={14} className="text-[#D92D20]" />
            <p className="text-[0.65rem] uppercase tracking-[0.2em] text-neutral-400">Idea reel</p>
            {idea.tema && <span className="text-[0.65rem] px-2 py-0.5 rounded-full border border-[#262626] text-neutral-300">{idea.tema}</span>}
          </div>
          <p className="font-display font-bold text-white leading-snug">{idea.hook}</p>
          <p className="mt-1.5 text-sm text-neutral-300 leading-relaxed">{idea.angolo}</p>
        </div>
      )}
    </div>
  );
}

export default function Commenti() {
  const [items, setItems] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [filter, setFilter] = useState("tutti");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await listCommenti({ days: 60 })); } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel caricamento"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); listScripts().then((s) => setScripts(s.slice(0, 40))).catch(() => {}); }, [load]);

  const onNew = (nuovi) => setItems((prev) => [...nuovi, ...prev]);
  const onChange = (c) => setItems((prev) => prev.map((x) => (x.id === c.id ? c : x)));
  const onDelete = (id) => setItems((prev) => prev.filter((x) => x.id !== id));

  const visibili = items.filter((c) => (filter === "tutti" ? true : filter === "usati" ? c.usato : !c.usato));
  const daUsare = items.filter((c) => !c.usato).length;
  const conIdea = items.filter((c) => !c.usato && c.idea?.spunto).length;

  return (
    <div data-testid="commenti-page">
    <Ceste />
    <div className="grid lg:grid-cols-[1fr_320px] gap-8" data-testid="commenti-screenshot">
      <div>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-[0.65rem] uppercase tracking-[0.25em] text-neutral-500 mb-1">Screenshot · DM e commenti singoli</p>
            <h2 className="font-display font-extrabold text-2xl text-white">Cosa vi scrivono</h2>
          </div>
          <div className="flex items-center gap-1 text-xs" data-testid="commenti-filter">
            {[["tutti", "Tutti"], ["da_usare", `Da usare (${daUsare})`], ["usati", "Usati"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`commenti-filter-${k}`}
                className={`px-3 py-1.5 rounded-md border transition-colors ${filter === k ? "border-[#D92D20] text-white" : "border-[#262626] text-neutral-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>
        </div>

        <UploadBox scripts={scripts} onDone={onNew} />

        <div className="mt-6 space-y-3">
          {loading && <div className="flex items-center gap-3 text-neutral-400 text-sm py-6" data-testid="commenti-loading"><Loader2 size={16} className="animate-spin" /> Carico…</div>}
          {!loading && visibili.length === 0 && (
            <div className="rounded-lg border border-dashed border-[#262626] p-10 text-center text-sm text-neutral-500" data-testid="commenti-empty">
              <MessageSquareText size={22} className="mx-auto mb-3 text-neutral-600" />
              {items.length === 0 ? "Ancora nessun commento. Carica il primo screenshot qui sopra." : "Niente in questa vista."}
            </div>
          )}
          {visibili.map((c) => <CommentCard key={c.id} c={c} onChange={onChange} onDelete={onDelete} />)}
        </div>
      </div>

      <aside className="space-y-4">
        <div className="rounded-lg border border-[#262626] bg-[#111] p-5 text-sm" data-testid="commenti-riepilogo">
          <h3 className="font-display font-bold text-sm text-white mb-3">A che punto siamo</h3>
          <p className="text-neutral-400"><span className="text-white font-semibold">{daUsare}</span> commenti da usare</p>
          <p className="text-neutral-400 mt-1"><span className="text-white font-semibold">{conIdea}</span> con un'idea reel pronta</p>
        </div>
        <div className="rounded-lg border border-[#262626] bg-[#111] p-5 text-xs text-neutral-400 leading-relaxed" data-testid="commenti-come-funziona">
          <h3 className="font-display font-bold text-sm text-white mb-2">Come funziona</h3>
          <p><span className="text-white font-semibold">1</span> fai lo screenshot dei commenti (o del DM) sul telefono · <span className="text-white font-semibold">2</span> lo carichi qui, anche 5-6 insieme · <span className="text-white font-semibold">3</span> copi la risposta e la incolli sotto il reel.</p>
          <p className="mt-2"><span className="text-neutral-200">Il venerdì</span>, quando facciamo il piano della settimana, leggo tutto quello che hai caricato e pesco anche da qui. Quando un commento è diventato un reel, segnalo <span className="text-neutral-200">Usato</span>.</p>
        </div>
        <div className="rounded-lg border border-[#262626] bg-[#111] p-5 text-xs text-neutral-400 leading-relaxed" data-testid="commenti-regole">
          <h3 className="font-display font-bold text-sm text-white mb-2">Le risposte</h3>
          Corte, come le scriveresti col pollice. Mai contro la persona: prima la sua storia con le sue parole, poi una frase sul sistema. Zero politica, zero vendita, zero «scrivimi in privato».
        </div>
      </aside>
    </div>
    </div>
  );
}
